import { useQuery } from 'react-query';
import { getActivityLiveStatus, ActivityLiveStatus } from './activityLive';
import useActivities from '.';

type UseActivitiesLiveStatus = (eventUniqueName: string) => {
    data: Record<string, ActivityLiveStatus>;
    error: Error;
    isLoading: boolean;
    isIdle: boolean;
};

export const useActivitiesLiveStatus: UseActivitiesLiveStatus = eventUniqueName => {
    const activities = useActivities(eventUniqueName);

    return useQuery(
        ['activities_live_status', eventUniqueName],
        async () =>
            activities.data.reduce((items, activity) => {
                items[`${activity.id}`] = getActivityLiveStatus(
                    activity.startDateUTCTimestamp,
                    activity.endDateUTCTimestamp
                );
                return items;
            }, {}),
        {
            enabled: !activities.isLoading && !!activities.data,
            refetchInterval: 1000 * 60 * 0.5, // recheck twice per minute
            notifyOnChangeProps: 'tracked',
        }
    );
};

export const useIsActivityLive = (eventUniqueName: string, id: string | number) => {
    const statuses = useActivitiesLiveStatus(eventUniqueName);
    if (statuses.isIdle || statuses.isLoading || statuses.error || !statuses.data) {
        return false;
    }

    return statuses.data[`${id}`]?.isLive;
};

export const useIsActivityActive = (eventUniqueName: string, id: string | number) => {
    const statuses = useActivitiesLiveStatus(eventUniqueName);
    if (statuses.isIdle || statuses.isLoading || statuses.error || !statuses.data) {
        return false;
    }

    return statuses.data[`${id}`]?.isActive;
};

export const useIsCollaborationActivityActive = (eventUniqueName: string, id: string | number) => {
    const statuses = useActivitiesLiveStatus(eventUniqueName);
    if (statuses.isIdle || statuses.isLoading || statuses.error || !statuses.data) {
        return false;
    }

    return statuses.data[`${id}`]?.isCollaborationActive;
};

export const useIsActivityFinished = (eventUniqueName: string, id: string | number) => {
    const statuses = useActivitiesLiveStatus(eventUniqueName);
    if (statuses.isIdle || statuses.isLoading || statuses.error || !statuses.data) {
        return false;
    }

    return statuses.data[`${id}`]?.isFinished;
};

export const useIsActivityInPast = (eventUniqueName: string, id: string | number) => {
    const statuses = useActivitiesLiveStatus(eventUniqueName);
    if (statuses.isIdle || statuses.isLoading || statuses.error || !statuses.data) {
        return false;
    }

    return statuses.data[`${id}`]?.isInPast;
};

export const useIsActivityAboutToFinish = (eventUniqueName: string, id: string | number) => {
    const statuses = useActivitiesLiveStatus(eventUniqueName);
    if (statuses.isIdle || statuses.isLoading || statuses.error || !statuses.data) {
        return false;
    }

    return statuses.data[`${id}`]?.isAboutToFinish;
};

export const useShouldTrackAttendance = (eventUniqueName: string, id: string | number) => {
    const statuses = useActivitiesLiveStatus(eventUniqueName);
    if (statuses.isIdle || statuses.isLoading || statuses.error || !statuses.data) {
        return false;
    }

    return statuses.data[`${id}`]?.shouldTrackAttendance;
};

export default useIsActivityLive;
